import { useCallback, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { useLazyGetEntitySchemaQuery, useLazyGetPartyDetailsQuery } from '../../../store/integrations/integrations'
import { useExecuteAiAgentMutation } from '../../../store/ai/aiAgent'
import {
  AccountOpeningCollections,
  type AccountOpeningCollection,
  type AccountOpeningCollectionStep,
  type AccountOpeningStep,
} from '../../../models/AccountOpeningDescriptor'
import {
  collectionSchemaSource,
  itemNeedsCustodianDetails,
  itemNeedsSchema,
  itemSchemaKey,
  itemsOf,
  prefillSourcesFor,
  prefillWanted,
  stepItems,
  type CollectionContext,
  type ResolvedItem,
} from '../domain/collections'
import { contactFacts } from '../domain/contactFallback'
import { fetchPrefilled, prefillHasSource, type PrefillOutcome, type PrefillResults } from '../domain/prefillSources'
import { type ContactOption, type WizardContext } from '../domain/types'
import { partySchemaRole } from '../domain/parties'
import { type CustodianDetails, type DraftAction, type FetchedSchema, type WizardDraft } from '../domain/wizardDraft'

export class CustodianDetailsUnavailable extends Error {}

export interface PreparedSchemas {
  items: ResolvedItem[]
  schemas: Map<string, Record<string, unknown>>
}

export interface CollectionSchemas {
  hydrateCollection: (collection: AccountOpeningCollection) => Promise<PreparedSchemas>
  prefillCollection: (collection: AccountOpeningCollection, prepared: PreparedSchemas) => Promise<void>
  fillValues: (collection: AccountOpeningCollection, filled: PrefillResults) => void
}

export const useCollectionSchemas = ({
  ctx,
  collectionCtx,
  steps,
  readOnly,
  contactOptions,
  partyDetailsEntity,
  apply,
}: {
  ctx: WizardContext
  collectionCtx: CollectionContext
  steps: AccountOpeningStep[]
  readOnly: boolean
  contactOptions: ContactOption[]
  partyDetailsEntity?: string
  apply: (action: DraftAction) => WizardDraft
}): CollectionSchemas => {
  const [fetchEntitySchema] = useLazyGetEntitySchemaQuery()
  const [fetchPartyDetails] = useLazyGetPartyDetailsQuery()
  const [executePrefill] = useExecuteAiAgentMutation()

  const contactFactsById = useMemo(() => new Map(contactOptions.map((option) => [option.id, contactFacts(option)])), [contactOptions])

  const hydrateSchemas = useCallback(
    async (collection: AccountOpeningCollection, items: ResolvedItem[]): Promise<Map<string, FetchedSchema>> => {
      const source = collectionSchemaSource(steps, collection)
      const fetched = new Map<string, FetchedSchema>()
      if (!source) {
        return fetched
      }
      await Promise.all(
        items.map(async (item) => {
          if (!itemNeedsSchema(item, source.valueFrom)) {
            return
          }
          const response = await fetchEntitySchema({
            accountId: ctx.accountId,
            connector: ctx.connector,
            entity: source.entity,
            keyedBy: source.keyedBy,
            key: itemSchemaKey(item, source.valueFrom),
          }).unwrap()
          fetched.set(item.ref, { schema: response.data ?? {}, fetchedAt: new Date().toISOString() })
        }),
      )
      return fetched
    },
    [steps, ctx.accountId, ctx.connector, fetchEntitySchema],
  )

  const prefillStepFor = useCallback(
    (collection: AccountOpeningCollection) =>
      steps.find((step): step is AccountOpeningCollectionStep => step.kind === 'collection' && step.collection === collection && !!step.prefill),
    [steps],
  )

  const runPrefills = useCallback(
    async (collection: AccountOpeningCollection, items: ResolvedItem[], schemas: Map<string, Record<string, unknown>>) => {
      const prefillStep = prefillStepFor(collection)
      const prefill = prefillStep?.prefill
      if (!prefillStep || !prefill || readOnly) {
        return new Map<string, PrefillOutcome>()
      }
      const withFetchedSchema = (item: ResolvedItem): ResolvedItem => {
        const schema = schemas.get(item.ref)
        if (!schema) {
          return item
        }
        return item.collection === AccountOpeningCollections.ACCOUNTS
          ? { ...item, account: { ...item.account, schemaSnapshot: schema } }
          : { ...item, party: { ...item.party, kycSchemaSnapshot: schema } }
      }
      const shownRefs = new Set(stepItems(prefillStep, collectionCtx).map((item) => item.ref))
      const targets = items
        .filter((item) => shownRefs.has(item.ref))
        .map(withFetchedSchema)
        .filter((item) => prefillWanted(prefillStep, item))
        .map((item) => ({ key: item.ref, sources: prefillSourcesFor(item, ctx, prefillStep) }))
        .filter((target) => prefillHasSource(prefill, target.sources))

      const { filled } = await fetchPrefilled({ executePrefill, accountId: ctx.accountId, prefill, targets })
      return filled
    },
    [prefillStepFor, readOnly, ctx, collectionCtx, executePrefill],
  )

  const hydrateCollection = useCallback(
    async (collection: AccountOpeningCollection): Promise<PreparedSchemas> => {
      const items = itemsOf(collection, collectionCtx)
      const fetched = await hydrateSchemas(collection, items)
      if (fetched.size > 0) {
        apply({ type: 'schemasFetched', collection, schemas: fetched })
      }
      return { items, schemas: new Map([...fetched].map(([ref, entry]) => [ref, entry.schema])) }
    },
    [collectionCtx, hydrateSchemas, apply],
  )

  const fillValues = useCallback(
    (collection: AccountOpeningCollection, filled: PrefillResults) => {
      if (readOnly) {
        return
      }
      apply({
        type: 'valuesFilled',
        collection,
        values: new Map([...filled].map(([ref, outcome]) => [ref, outcome.values])),
        citations: new Map([...filled].map(([ref, outcome]) => [ref, outcome.citations])),
        contactFacts: contactFactsById,
      })
    },
    [apply, readOnly, contactFactsById],
  )

  const readCustodianDetails = useCallback(
    async (items: ResolvedItem[]): Promise<Map<string, CustodianDetails>> => {
      const details = new Map<string, CustodianDetails>()
      const outstanding = items.filter(itemNeedsCustodianDetails)
      if (readOnly || outstanding.length === 0) {
        return details
      }
      // Existing clients are neither reviewed nor prefilled, so a missing read must fail rather than send them empty.
      if (!partyDetailsEntity) {
        const unavailable = new CustodianDetailsUnavailable('The account-opening flow names no partyDetails entity to read existing clients back with.')
        Sentry.captureException(unavailable)
        throw unavailable
      }
      await Promise.all(
        outstanding.map(async (item) => {
          if (item.collection !== AccountOpeningCollections.PARTIES || !item.party.externalId) {
            return
          }
          const externalId = item.party.externalId
          const response = await fetchPartyDetails({
            accountId: ctx.accountId,
            connector: ctx.connector,
            entity: partyDetailsEntity,
            externalId,
            role: partySchemaRole(item.party) ?? '',
          })
            .unwrap()
            .catch(() => {
              throw new CustodianDetailsUnavailable(`The custodian details of existing client ${externalId} could not be read.`)
            })
          const values = response.data ?? {}
          if (Object.keys(values).length === 0) {
            throw new CustodianDetailsUnavailable(`The custodian returned no details for existing client ${externalId}.`)
          }
          details.set(item.ref, { externalId, values, fetchedAt: new Date().toISOString() })
        }),
      )
      return details
    },
    [partyDetailsEntity, readOnly, ctx.accountId, ctx.connector, fetchPartyDetails],
  )

  const prefillCollection = useCallback(
    async (collection: AccountOpeningCollection, prepared: PreparedSchemas) => {
      const [filled, details] = await Promise.all([
        runPrefills(collection, prepared.items, prepared.schemas),
        collection === AccountOpeningCollections.PARTIES ? readCustodianDetails(prepared.items) : Promise.resolve(new Map<string, CustodianDetails>()),
      ])
      if (details.size > 0) {
        apply({ type: 'custodianDetailsFetched', details })
      }
      if (filled.size === 0 && collection === AccountOpeningCollections.ACCOUNTS) {
        return
      }
      fillValues(collection, filled)
    },
    [runPrefills, readCustodianDetails, apply, fillValues],
  )

  return { hydrateCollection, prefillCollection, fillValues }
}
